import { SortDirection } from "@redotech/redo-views-common/table";
import { FulfillmentGroupColumnKey } from "../../fulfillments/fulfillment-group-column-keys";
import { ColumnCatalogEntry } from "./column-catalog-entry";

// ── Individual column entries ────────────────────────────────────────────────
// Each key matches the Column2.key used in the frontend column files.
// The frontend can import these entries and use them as the source of truth
// for title, key, sortDirection, and width.

export const fgColumnEntries = {
  [FulfillmentGroupColumnKey.SELECTED]: {
    key: FulfillmentGroupColumnKey.SELECTED,
    title: "Selected",
    sortDirection: undefined,
    defaultWidth: 24,
    agentDescription:
      "Checkbox column for selecting rows. Always included as the first column. Not useful to add or remove — it is always present.",
  },

  [FulfillmentGroupColumnKey.ADDRESS_VERIFICATION]: {
    key: FulfillmentGroupColumnKey.ADDRESS_VERIFICATION,
    title: "Address verification",
    sortDirection: SortDirection.DESC,
    defaultWidth: 160,
    agentDescription:
      "Shows whether the shipping address has been verified. Useful for views focused on address quality or flagging potential delivery issues.",
  },

  [FulfillmentGroupColumnKey.ASSIGNED_USER]: {
    key: FulfillmentGroupColumnKey.ASSIGNED_USER,
    title: "Assigned user",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The user assigned to this fulfillment group. Useful for views where merchants want to see who is responsible for fulfilling orders.",
  },

  [FulfillmentGroupColumnKey.BATCH_ID]: {
    key: FulfillmentGroupColumnKey.BATCH_ID,
    title: "Batch ID",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The batch ID if the order was processed in a batch. Useful for tracking batch fulfillment workflows.",
  },

  [FulfillmentGroupColumnKey.BIN]: {
    key: FulfillmentGroupColumnKey.BIN,
    title: "Bin",
    sortDirection: SortDirection.DESC,
    defaultWidth: 80,
    agentDescription:
      "The warehouse bin location for this order. Useful for WMS/warehouse views to see where items are stored.",
  },

  [FulfillmentGroupColumnKey.CUSTOMER]: {
    key: FulfillmentGroupColumnKey.CUSTOMER,
    title: "Customer",
    sortDirection: SortDirection.DESC,
    defaultWidth: 160,
    agentDescription:
      "The customer's name. One of the most common columns — useful in almost any view to identify who placed the order.",
  },

  [FulfillmentGroupColumnKey.CUSTOMER_EMAIL]: {
    key: FulfillmentGroupColumnKey.CUSTOMER_EMAIL,
    title: "Customer email",
    sortDirection: undefined,
    defaultWidth: 200,
    agentDescription:
      "The customer's email address. Useful when merchants need to contact customers or look up orders by email.",
  },

  [FulfillmentGroupColumnKey.CUSTOMER_PHONE]: {
    key: FulfillmentGroupColumnKey.CUSTOMER_PHONE,
    title: "Customer phone",
    sortDirection: undefined,
    defaultWidth: 160,
    agentDescription:
      "The customer's phone number. Useful when merchants need to call customers or look up orders by phone number.",
  },

  [FulfillmentGroupColumnKey.CUSTOMER_ID]: {
    key: FulfillmentGroupColumnKey.CUSTOMER_ID,
    title: "Customer id",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The customer's unique identifier. Useful for views that need to cross-reference customers across systems.",
  },

  [FulfillmentGroupColumnKey.CUSTOMER_ORDER_COUNT]: {
    key: FulfillmentGroupColumnKey.CUSTOMER_ORDER_COUNT,
    title: "Customer order count",
    sortDirection: SortDirection.DESC,
    defaultWidth: 160,
    agentDescription:
      "How many orders this customer has placed. Useful for identifying repeat customers or VIP order views.",
  },

  [FulfillmentGroupColumnKey.CUSTOMER_PO]: {
    key: FulfillmentGroupColumnKey.CUSTOMER_PO,
    title: "Customer PO",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The customer's purchase order number. Common in B2B/wholesale views where PO numbers are used for tracking.",
  },

  [FulfillmentGroupColumnKey.SHIPPING_SELECTED]: {
    key: FulfillmentGroupColumnKey.SHIPPING_SELECTED,
    title: "Customer shipping",
    sortDirection: undefined,
    defaultWidth: 100,
    agentDescription:
      "The shipping method the customer selected at checkout. Useful for views comparing customer-requested vs actual shipping.",
  },

  [FulfillmentGroupColumnKey.DELIVERY_METHOD]: {
    key: FulfillmentGroupColumnKey.DELIVERY_METHOD,
    title: "Delivery method",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The delivery method type (e.g., shipping, local pickup, local delivery). Useful for filtering views by how orders are fulfilled.",
  },

  [FulfillmentGroupColumnKey.DELIVERY_STATUS]: {
    key: FulfillmentGroupColumnKey.DELIVERY_STATUS,
    title: "Delivery status",
    sortDirection: SortDirection.DESC,
    defaultWidth: 160,
    agentDescription:
      "The tracking/delivery status (e.g., in transit, delivered, exception). Useful for views monitoring post-shipment delivery progress.",
  },

  [FulfillmentGroupColumnKey.DESTINATION_COUNTRY]: {
    key: FulfillmentGroupColumnKey.DESTINATION_COUNTRY,
    title: "Country",
    sortDirection: undefined,
    defaultWidth: 80,
    agentDescription:
      "The destination country code. Useful for international shipping views or when filtering by domestic vs. international orders.",
  },

  [FulfillmentGroupColumnKey.ELECTRONIC_COM_INVOICE]: {
    key: FulfillmentGroupColumnKey.ELECTRONIC_COM_INVOICE,
    title: "Comm. invoice e-submitted",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "Whether the commercial invoice was electronically submitted. Relevant for international shipments that require customs documentation.",
  },

  [FulfillmentGroupColumnKey.ESTIMATED_DELIVERY_DATE]: {
    key: FulfillmentGroupColumnKey.ESTIMATED_DELIVERY_DATE,
    title: "Est. delivery date",
    sortDirection: SortDirection.DESC,
    defaultWidth: 160,
    agentDescription:
      "The estimated delivery date for the shipment. Useful for views tracking delivery timelines or SLA compliance.",
  },

  [FulfillmentGroupColumnKey.FRAUD_RISK]: {
    key: FulfillmentGroupColumnKey.FRAUD_RISK,
    title: "Fraud risk",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The fraud risk assessment level for the order. Useful for views that help merchants review potentially fraudulent orders before fulfilling.",
  },

  [FulfillmentGroupColumnKey.FULFILLED_BY]: {
    key: FulfillmentGroupColumnKey.FULFILLED_BY,
    title: "Fulfilled by",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "Who fulfilled the order (e.g., merchant, 3PL). Useful for views where multiple fulfillment services handle orders.",
  },

  [FulfillmentGroupColumnKey.FULFILLMENT_LOCATION]: {
    key: FulfillmentGroupColumnKey.FULFILLMENT_LOCATION,
    title: "Fulfillment location",
    sortDirection: SortDirection.ASC,
    defaultWidth: 240,
    agentDescription:
      "The warehouse or fulfillment location handling this order. Useful for multi-location merchants to see which location is responsible.",
  },

  [FulfillmentGroupColumnKey.INSURED]: {
    key: FulfillmentGroupColumnKey.INSURED,
    title: "Shipping insurance",
    sortDirection: undefined,
    defaultWidth: 140,
    agentDescription:
      "Whether shipping insurance is applied. Useful for views tracking insured shipments.",
  },

  [FulfillmentGroupColumnKey.INTERNAL_NOTES]: {
    key: FulfillmentGroupColumnKey.INTERNAL_NOTES,
    title: "Internal notes",
    sortDirection: undefined,
    defaultWidth: 310,
    agentDescription:
      "Internal notes added by the merchant's team. Useful for views where team members communicate about orders.",
  },

  [FulfillmentGroupColumnKey.PACKING_SLIP_PRINTED]: {
    key: FulfillmentGroupColumnKey.PACKING_SLIP_PRINTED,
    title: "Packing slip printed",
    sortDirection: SortDirection.ASC,
    defaultWidth: 120,
    agentDescription:
      "Whether the packing slip has been printed. Useful for warehouse workflow views to track print status.",
  },

  [FulfillmentGroupColumnKey.LABEL_PRINTED]: {
    key: FulfillmentGroupColumnKey.LABEL_PRINTED,
    title: "Label printed",
    sortDirection: SortDirection.ASC,
    defaultWidth: 120,
    agentDescription:
      "Whether the shipping label has been printed. Useful for warehouse workflow views to track print status.",
  },

  [FulfillmentGroupColumnKey.PICK_LIST_PRINTED]: {
    key: FulfillmentGroupColumnKey.PICK_LIST_PRINTED,
    title: "Pick list printed",
    sortDirection: SortDirection.ASC,
    defaultWidth: 120,
    agentDescription:
      "Whether the pick list has been printed. Useful for WMS views tracking the picking workflow.",
  },

  [FulfillmentGroupColumnKey.ITEM_NAME]: {
    key: FulfillmentGroupColumnKey.ITEM_NAME,
    title: "Items",
    sortDirection: SortDirection.DESC,
    defaultWidth: 160,
    agentDescription:
      "The item/product names in the order. Useful for views where merchants want to see what products are being shipped.",
  },

  [FulfillmentGroupColumnKey.ITEM_SKU]: {
    key: FulfillmentGroupColumnKey.ITEM_SKU,
    title: "Item SKU",
    sortDirection: SortDirection.DESC,
    defaultWidth: 80,
    agentDescription:
      "The item SKU(s) in the order. Useful for warehouse views where merchants identify items by SKU rather than name.",
  },

  [FulfillmentGroupColumnKey.LABEL_PURCHASED_AT]: {
    key: FulfillmentGroupColumnKey.LABEL_PURCHASED_AT,
    title: "Label purchased at",
    sortDirection: SortDirection.DESC,
    defaultWidth: 80,
    agentDescription:
      "When the shipping label was purchased. Useful for views tracking label purchasing timelines.",
  },

  [FulfillmentGroupColumnKey.LINE_ITEM_CUSTOM_ATTRIBUTES]: {
    key: FulfillmentGroupColumnKey.LINE_ITEM_CUSTOM_ATTRIBUTES,
    title: "Line item custom attributes",
    sortDirection: undefined,
    defaultWidth: 240,
    agentDescription:
      "Custom attributes attached to line items (key/value pairs from the source marketplace/cart). Useful for views that need to see per-line-item metadata like personalization, engravings, or gift messages.",
  },

  [FulfillmentGroupColumnKey.MARKETPLACE_INTERNAL_NOTE]: {
    key: FulfillmentGroupColumnKey.MARKETPLACE_INTERNAL_NOTE,
    title: "Marketplace internal note",
    sortDirection: undefined,
    defaultWidth: 310,
    agentDescription:
      "Internal notes from the marketplace/sales channel. Useful for orders that come with special instructions from the marketplace.",
  },

  [FulfillmentGroupColumnKey.MARKETPLACE_NOTIFICATION_STATUS]: {
    key: FulfillmentGroupColumnKey.MARKETPLACE_NOTIFICATION_STATUS,
    title: "Marketplace notification",
    sortDirection: undefined,
    defaultWidth: 160,
    agentDescription:
      "The marketplace notification status (e.g., pending, complete, skipped, failed). Shows whether the marketplace has been notified about the fulfillment.",
  },

  [FulfillmentGroupColumnKey.MISSING_BINS]: {
    key: FulfillmentGroupColumnKey.MISSING_BINS,
    title: "Missing bins",
    sortDirection: SortDirection.ASC,
    defaultWidth: 100,
    agentDescription:
      "Whether the order has items with missing bin assignments. Useful for WMS views to identify orders that need bin assignment.",
  },

  [FulfillmentGroupColumnKey.MISSING_PICK_ITEMS]: {
    key: FulfillmentGroupColumnKey.MISSING_PICK_ITEMS,
    title: "Missing pick items",
    sortDirection: SortDirection.DESC,
    defaultWidth: 200,
    agentDescription:
      "Items that are missing from the pick. Useful for WMS views tracking pick accuracy and exceptions.",
  },

  [FulfillmentGroupColumnKey.NOTE_FROM_BUYER]: {
    key: FulfillmentGroupColumnKey.NOTE_FROM_BUYER,
    title: "Order note",
    sortDirection: undefined,
    defaultWidth: 310,
    agentDescription:
      "Notes the customer added to the order. Useful for views where merchants need to see special customer instructions.",
  },

  [FulfillmentGroupColumnKey.NOTE_TO_BUYER]: {
    key: FulfillmentGroupColumnKey.NOTE_TO_BUYER,
    title: "Note to buyer",
    sortDirection: undefined,
    defaultWidth: 310,
    agentDescription:
      "Notes from the merchant to the customer. Useful for views tracking customer communication.",
  },

  [FulfillmentGroupColumnKey.TOTAL_SKU_COUNT]: {
    key: FulfillmentGroupColumnKey.TOTAL_SKU_COUNT,
    title: "Unique product count",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The number of unique products (line items) in the order. Useful for views identifying complex multi-item orders.",
  },

  [FulfillmentGroupColumnKey.ORDER_AGE]: {
    key: FulfillmentGroupColumnKey.ORDER_AGE,
    title: "Order age",
    sortDirection: SortDirection.DESC,
    defaultWidth: 100,
    agentDescription:
      "How old the order is. Useful for views that prioritize aging orders or track fulfillment speed.",
  },

  [FulfillmentGroupColumnKey.ORDER_DATE]: {
    key: FulfillmentGroupColumnKey.ORDER_DATE,
    title: "Order date",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "When the order was placed. One of the most common columns — useful in almost any view for chronological context.",
  },

  [FulfillmentGroupColumnKey.QUANTITY]: {
    key: FulfillmentGroupColumnKey.QUANTITY,
    title: "Quantity",
    sortDirection: SortDirection.DESC,
    defaultWidth: 80,
    agentDescription:
      "The total item quantity in the order. Useful for views tracking order sizes or warehouse workload.",
  },

  [FulfillmentGroupColumnKey.ORDER_NAME]: {
    key: FulfillmentGroupColumnKey.ORDER_NAME,
    title: "Order",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The order number/name (e.g., #1001). One of the most essential columns — almost always included to identify orders.",
  },

  [FulfillmentGroupColumnKey.ORDER_SOURCE]: {
    key: FulfillmentGroupColumnKey.ORDER_SOURCE,
    title: "Order source",
    sortDirection: undefined,
    defaultWidth: 100,
    agentDescription:
      "Where the order originated (e.g., web, POS, draft). Useful for views segmenting orders by how they were placed.",
  },

  [FulfillmentGroupColumnKey.TOTAL]: {
    key: FulfillmentGroupColumnKey.TOTAL,
    title: "Total",
    sortDirection: SortDirection.DESC,
    defaultWidth: 100,
    agentDescription:
      "The order total price including shipping and taxes. Useful for views that sort or display orders by value.",
  },

  [FulfillmentGroupColumnKey.ORDER_SUBTOTAL]: {
    key: FulfillmentGroupColumnKey.ORDER_SUBTOTAL,
    title: "Order subtotal",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The order subtotal (after discounts, before tax). Useful for views breaking down order value into pre-tax and tax components.",
  },

  [FulfillmentGroupColumnKey.TAX_TOTAL]: {
    key: FulfillmentGroupColumnKey.TAX_TOTAL,
    title: "Tax total",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The total tax charged on the order. Useful for views breaking down order value into pre-tax and tax components.",
  },

  [FulfillmentGroupColumnKey.WEIGHT]: {
    key: FulfillmentGroupColumnKey.WEIGHT,
    title: "Total weight",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The total weight of the order. Useful for shipping-focused views or identifying heavy orders.",
  },

  [FulfillmentGroupColumnKey.ORIGIN_STORE]: {
    key: FulfillmentGroupColumnKey.ORIGIN_STORE,
    title: "Store",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The origin store or shop. Useful for multi-store merchants to see which store the order came from.",
  },

  [FulfillmentGroupColumnKey.PACKING_SLIP_PRINTED_AT]: {
    key: FulfillmentGroupColumnKey.PACKING_SLIP_PRINTED_AT,
    title: "Packing slip printed at",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "When the packing slip was printed. Useful for warehouse workflow views tracking print timestamps.",
  },

  [FulfillmentGroupColumnKey.PAYMENT_TYPE]: {
    key: FulfillmentGroupColumnKey.PAYMENT_TYPE,
    title: "Payment type",
    sortDirection: undefined,
    defaultWidth: 160,
    agentDescription:
      "The payment method used for the order. Useful for views segmenting orders by payment type (e.g., COD, prepaid).",
  },

  [FulfillmentGroupColumnKey.PICKABILITY]: {
    key: FulfillmentGroupColumnKey.PICKABILITY,
    title: "Pickability",
    sortDirection: undefined,
    defaultWidth: 160,
    agentDescription:
      "Whether all items in the order have sufficient inventory in pickable bins. Shows per-item detail with bin breakdown when expanded.",
  },

  [FulfillmentGroupColumnKey.PICKABLE_BIN_COUNT]: {
    key: FulfillmentGroupColumnKey.PICKABLE_BIN_COUNT,
    title: "Pickable bin count",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "Count of distinct pickable bins (type 'bin') each item is assigned to. The top-level value is the sum across line items; expanded rows show each item's count.",
  },

  [FulfillmentGroupColumnKey.WAREHOUSE_BINS]: {
    key: FulfillmentGroupColumnKey.WAREHOUSE_BINS,
    title: "Warehouse bins",
    sortDirection: undefined,
    defaultWidth: 160,
    agentDescription:
      "The warehouse bin locations for each item, sourced from the same data the Pickability column shows. WO_STAGING and empty receiving/storage bins are excluded. Useful for WMS views that need to see bin assignments without quantity or replenishment detail.",
  },

  [FulfillmentGroupColumnKey.PRODUCT_BIN_ASSIGNMENT_DATE]: {
    key: FulfillmentGroupColumnKey.PRODUCT_BIN_ASSIGNMENT_DATE,
    title: "Product bin assignment date",
    sortDirection: undefined,
    defaultWidth: 180,
    agentDescription:
      "When each product was first assigned to a warehouse bin, based on the earliest inventory_level created_at across each item's bin/storage locations (receiving is excluded). Expanded rows show each item's earliest assignment date; the collapsed value is the latest of those per-item dates. Blank unless every line item has at least one bin/storage assignment.",
  },

  [FulfillmentGroupColumnKey.PICK_STATUS]: {
    key: FulfillmentGroupColumnKey.PICK_STATUS,
    title: "Pick status",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The pick status for WMS workflows. Useful for warehouse views tracking whether orders have been picked.",
  },

  [FulfillmentGroupColumnKey.PICK_SESSION_ID]: {
    key: FulfillmentGroupColumnKey.PICK_SESSION_ID,
    title: "Pick session ID",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The pick session this order belongs to. Useful for WMS views grouping orders by pick session.",
  },

  [FulfillmentGroupColumnKey.PICK_STARTED_ON]: {
    key: FulfillmentGroupColumnKey.PICK_STARTED_ON,
    title: "Pick started on",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "When picking started for this order. Useful for WMS views tracking pick timing and throughput.",
  },

  [FulfillmentGroupColumnKey.PICKED_AT]: {
    key: FulfillmentGroupColumnKey.PICKED_AT,
    title: "Picked on",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "When the order was fully picked. Useful for WMS views tracking pick completion times.",
  },

  [FulfillmentGroupColumnKey.ASSIGNED_PICKER]: {
    key: FulfillmentGroupColumnKey.ASSIGNED_PICKER,
    title: "Assigned picker",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The warehouse picker assigned to this order. Useful for WMS views tracking picker workload.",
  },

  [FulfillmentGroupColumnKey.ASSIGNED_TOTES]: {
    key: FulfillmentGroupColumnKey.ASSIGNED_TOTES,
    title: "Assigned totes",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The totes assigned during picking. Useful for WMS views tracking tote usage in pick workflows.",
  },

  [FulfillmentGroupColumnKey.POSTAL_CODE]: {
    key: FulfillmentGroupColumnKey.POSTAL_CODE,
    title: "Postal code",
    sortDirection: SortDirection.DESC,
    defaultWidth: 100,
    agentDescription:
      "The destination postal/zip code. Useful for views grouping orders by destination region.",
  },

  [FulfillmentGroupColumnKey.PRODUCT_TAGS]: {
    key: FulfillmentGroupColumnKey.PRODUCT_TAGS,
    title: "Product tags",
    sortDirection: SortDirection.ASC,
    defaultWidth: 120,
    agentDescription:
      "Tags assigned to the products in the order. Useful for views filtering by product categorization.",
  },

  [FulfillmentGroupColumnKey.PRODUCT_VARIANT]: {
    key: FulfillmentGroupColumnKey.PRODUCT_VARIANT,
    title: "Product variant",
    sortDirection: SortDirection.ASC,
    defaultWidth: 100,
    agentDescription:
      "The product variant (e.g., size, color). Useful for views that need to distinguish between product variations.",
  },

  [FulfillmentGroupColumnKey.RATE]: {
    key: FulfillmentGroupColumnKey.RATE,
    title: "Rate",
    sortDirection: SortDirection.DESC,
    defaultWidth: 80,
    agentDescription:
      "The shipping rate/cost. Useful for views tracking shipping costs or comparing rates.",
  },

  [FulfillmentGroupColumnKey.SHIPMENT_ADJUSTMENTS]: {
    key: FulfillmentGroupColumnKey.SHIPMENT_ADJUSTMENTS,
    title: "Shipment adjustments",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The total surcharge adjustments applied to this shipment. Useful for views tracking additional shipping costs beyond the base rate.",
  },

  [FulfillmentGroupColumnKey.RECIPIENT]: {
    key: FulfillmentGroupColumnKey.RECIPIENT,
    title: "Recipient",
    sortDirection: undefined,
    defaultWidth: 160,
    agentDescription:
      "The recipient's name (may differ from the customer). Useful when the shipping address is different from the billing customer.",
  },

  [FulfillmentGroupColumnKey.REQUESTED_SERVICE]: {
    key: FulfillmentGroupColumnKey.REQUESTED_SERVICE,
    title: "Requested service",
    sortDirection: SortDirection.DESC,
    defaultWidth: 160,
    agentDescription:
      "The shipping service requested (e.g., Priority Mail, Ground). Useful for views tracking which services were requested.",
  },

  [FulfillmentGroupColumnKey.SALES_CHANNEL]: {
    key: FulfillmentGroupColumnKey.SALES_CHANNEL,
    title: "Sales channels",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The sales channels the order came through. Useful for multi-channel merchants viewing orders by channel.",
  },

  [FulfillmentGroupColumnKey.SCAN_VERIFIED]: {
    key: FulfillmentGroupColumnKey.SCAN_VERIFIED,
    title: "Scan verified",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "Whether the order was scan-verified during packing. Useful for warehouse quality control views.",
  },

  [FulfillmentGroupColumnKey.PARCEL_SELECTION]: {
    key: FulfillmentGroupColumnKey.PARCEL_SELECTION,
    title: "Package",
    sortDirection: SortDirection.DESC,
    defaultWidth: 160,
    agentDescription:
      "The selected package/parcel type. Useful for views tracking which packaging is used for orders.",
  },

  [FulfillmentGroupColumnKey.SHIPMENT_CREATED_AT]: {
    key: FulfillmentGroupColumnKey.SHIPMENT_CREATED_AT,
    title: "Shipment created at",
    sortDirection: SortDirection.DESC,
    defaultWidth: 400,
    agentDescription:
      "When the shipment record was created. Useful for views tracking fulfillment creation timelines.",
  },

  [FulfillmentGroupColumnKey.SHIPMENT_TAGS]: {
    key: FulfillmentGroupColumnKey.SHIPMENT_TAGS,
    title: "Shipment tags",
    sortDirection: SortDirection.DESC,
    defaultWidth: 150,
    agentDescription:
      "Tags assigned to the shipment in Redo OMS. Useful for views filtering by merchant-applied shipment tags.",
  },

  [FulfillmentGroupColumnKey.SHIPPED_DATE]: {
    key: FulfillmentGroupColumnKey.SHIPPED_DATE,
    title: "Fulfilled on",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "When the order was fulfilled/shipped. Useful for views tracking fulfillment completion dates.",
  },

  [FulfillmentGroupColumnKey.SHIPPING_MARGIN]: {
    key: FulfillmentGroupColumnKey.SHIPPING_MARGIN,
    title: "Shipping margin",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The margin between what the customer paid for shipping and the actual shipping cost. Useful for financial/profitability views.",
  },

  [FulfillmentGroupColumnKey.SHIPPING_PAID]: {
    key: FulfillmentGroupColumnKey.SHIPPING_PAID,
    title: "Shipping paid",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "How much the customer paid for shipping. Useful for financial views analyzing shipping revenue.",
  },

  [FulfillmentGroupColumnKey.SHIPPING_SELECTION]: {
    key: FulfillmentGroupColumnKey.SHIPPING_SELECTION,
    title: "Shipping selection",
    sortDirection: SortDirection.DESC,
    defaultWidth: 320,
    agentDescription:
      "The selected shipping carrier and service. One of the most common columns — shows which carrier/service is being used for the shipment.",
  },

  [FulfillmentGroupColumnKey.SHOPIFY_ORDER_TAGS]: {
    key: FulfillmentGroupColumnKey.SHOPIFY_ORDER_TAGS,
    title: "Order tags",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "Tags from Shopify/marketplace on the order. Useful for views that filter or display marketplace-level tags.",
  },

  [FulfillmentGroupColumnKey.SLA_DELIVERY_PROMISE]: {
    key: FulfillmentGroupColumnKey.SLA_DELIVERY_PROMISE,
    title: "Fulfillment promise",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "The SLA delivery promise/deadline. Useful for views tracking whether orders will meet their delivery commitments.",
  },

  [FulfillmentGroupColumnKey.STATE]: {
    key: FulfillmentGroupColumnKey.STATE,
    title: "State",
    sortDirection: SortDirection.DESC,
    defaultWidth: 100,
    agentDescription:
      "The destination state/province. Useful for views grouping orders by region within a country.",
  },

  [FulfillmentGroupColumnKey.STATUS]: {
    key: FulfillmentGroupColumnKey.STATUS,
    title: "Status",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The fulfillment status (e.g., Open, Closed, Pending). One of the most essential columns — almost always included.",
  },

  [FulfillmentGroupColumnKey.STREET_ADDRESS]: {
    key: FulfillmentGroupColumnKey.STREET_ADDRESS,
    title: "Street address",
    sortDirection: undefined,
    defaultWidth: 120,
    agentDescription:
      "The destination street address. Useful when merchants need to see full shipping addresses.",
  },

  [FulfillmentGroupColumnKey.TRACKING_NUMBER]: {
    key: FulfillmentGroupColumnKey.TRACKING_NUMBER,
    title: "Tracking number",
    sortDirection: undefined,
    defaultWidth: 250,
    agentDescription:
      "The shipment tracking number. Useful for views where merchants need to look up or share tracking info.",
  },

  [FulfillmentGroupColumnKey.USED_WEIGHT_LEARNING]: {
    key: FulfillmentGroupColumnKey.USED_WEIGHT_LEARNING,
    title: "Used weight learning",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "Whether weight learning was applied to this shipment. Useful for views analyzing the impact of automated weight predictions.",
  },

  [FulfillmentGroupColumnKey.USED_PARCEL_LEARNING]: {
    key: FulfillmentGroupColumnKey.USED_PARCEL_LEARNING,
    title: "Used package learning",
    sortDirection: SortDirection.DESC,
    defaultWidth: 120,
    agentDescription:
      "Whether package learning was applied to this shipment. Useful for views analyzing the impact of automated package selection.",
  },

  [FulfillmentGroupColumnKey.USPS_ZONE]: {
    key: FulfillmentGroupColumnKey.USPS_ZONE,
    title: "USPS zone",
    sortDirection: undefined,
    defaultWidth: 160,
    agentDescription:
      "The USPS shipping zone. Useful for views analyzing shipping costs by zone or distance.",
  },

  [FulfillmentGroupColumnKey.VENDORS]: {
    key: FulfillmentGroupColumnKey.VENDORS,
    title: "Vendors",
    sortDirection: SortDirection.DESC,
    defaultWidth: 200,
    agentDescription:
      "The product vendors. Useful for views segmenting orders by supplier/vendor.",
  },
} as const satisfies Record<string, ColumnCatalogEntry>;

// ── Default columns ──────────────────────────────────────────────────────────
// These are the columns shown by default when a view does not specify columns.

export const DEFAULT_FG_COLUMN_KEYS: readonly string[] = [
  FulfillmentGroupColumnKey.SELECTED,
  "is-packing-slip-printed",
  FulfillmentGroupColumnKey.STATUS,
  FulfillmentGroupColumnKey.ADDRESS_VERIFICATION,
  FulfillmentGroupColumnKey.ORDER_NAME,
  FulfillmentGroupColumnKey.CUSTOMER,
  FulfillmentGroupColumnKey.ORDER_DATE,
  FulfillmentGroupColumnKey.ITEM_SKU,
  FulfillmentGroupColumnKey.TOTAL,
  FulfillmentGroupColumnKey.QUANTITY,
  FulfillmentGroupColumnKey.WEIGHT,
  FulfillmentGroupColumnKey.PARCEL_SELECTION,
  FulfillmentGroupColumnKey.SHIPPING_SELECTION,
  FulfillmentGroupColumnKey.REQUESTED_SERVICE,
  FulfillmentGroupColumnKey.DESTINATION_COUNTRY,
  FulfillmentGroupColumnKey.FULFILLMENT_LOCATION,
];

// ── Catalog builder ──────────────────────────────────────────────────────────

export interface FGColumnCatalogOptions {
  wmsEnabled?: boolean;
}

export function getFGColumnCatalog(
  options: FGColumnCatalogOptions = {},
): ColumnCatalogEntry[] {
  const entries = Object.values(fgColumnEntries) as ColumnCatalogEntry[];

  const wmsKeys = new Set<string>([
    FulfillmentGroupColumnKey.BIN,
    FulfillmentGroupColumnKey.MISSING_BINS,
    FulfillmentGroupColumnKey.MISSING_PICK_ITEMS,
    FulfillmentGroupColumnKey.PICKABILITY,
    FulfillmentGroupColumnKey.PICKABLE_BIN_COUNT,
    FulfillmentGroupColumnKey.WAREHOUSE_BINS,
    FulfillmentGroupColumnKey.PICK_STATUS,
    FulfillmentGroupColumnKey.PICK_SESSION_ID,
    FulfillmentGroupColumnKey.PICK_STARTED_ON,
    FulfillmentGroupColumnKey.PICKED_AT,
    FulfillmentGroupColumnKey.ASSIGNED_PICKER,
    FulfillmentGroupColumnKey.ASSIGNED_TOTES,
  ]);

  if (!options.wmsEnabled) {
    return entries.filter((e) => !wmsKeys.has(e.key));
  }

  return entries;
}

export interface ColumnInput {
  key: string;
  width: number;
}

export interface ResolvedColumn {
  key: string;
  width: number;
  hidden: boolean;
}

const columnEntriesMap = fgColumnEntries as Record<
  string,
  { key: string; defaultWidth: number } | undefined
>;

export function resolveColumns(columns: ColumnInput[]): ResolvedColumn[] {
  if (columns.length === 0) {
    return DEFAULT_FG_COLUMN_KEYS.map((key) => {
      const entry = columnEntriesMap[key];
      return { key, width: entry?.defaultWidth ?? 120, hidden: false };
    });
  }

  const result: ResolvedColumn[] = [
    { key: "selected", width: 24, hidden: false },
  ];

  for (const col of columns) {
    if (col.key === "selected") {
      continue;
    }
    const entry = columnEntriesMap[col.key];
    result.push({
      key: col.key,
      width: col.width || entry?.defaultWidth || 120,
      hidden: false,
    });
  }

  return result;
}
